import { Instagram, Facebook, ArrowUp } from "lucide-react";
import TikTok from "@/components/TikTok";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";

const PAGE_LINKS = [
  { label: "FAQ", to: "/faq" },
  { label: "Privacy Policy", to: "/privacy" },
  { label: "Terms & Conditions", to: "/terms" },
  { label: "Accessibility", to: "/accessibility" },
  { label: "Refund Policy", to: "/refund" },
];

export default function Footer() {
  return (
    <footer data-testid="footer" className="relative border-t border-white/10 bg-[#0A0A0A]">
      <div className="border-b border-white/10 py-8">
        <Marquee speed={50} gradient={false}>
          <span className="font-display text-6xl uppercase text-white/[0.07] md:text-8xl">
            TRAINOVA&nbsp;—&nbsp;DOWNLOAD TRAINOVA ON iOS AND ANDROID NOW&nbsp;—&nbsp;
          </span>
        </Marquee>
      </div>

      <div className="mx-auto max-w-[1400px] px-6 py-16 md:px-12">
        <div className="flex flex-col justify-between gap-12 md:flex-row md:items-end">
          <div>
            <a href="#top" className="flex items-center gap-3">
              <img src="/trainova-logo.png" alt="Trainova" className="h-11 w-11 rounded-sm object-cover" />
              <span className="font-condensed text-3xl tracking-widest">TRAINOVA</span>
            </a>
            <p className="mt-6 max-w-sm text-zinc-500">
              The swipe-based fitness app that matches you with the perfect gym partner — by
              goals, experience level, location and more.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <a
                href="https://apps.apple.com/us/app/trainova-gym-partners/id6773907897"
                target="_blank"
                rel="noreferrer"
                data-testid="footer-appstore-btn"
                className="inline-flex items-center justify-center gap-2 bg-teal px-6 py-3 font-bold uppercase tracking-wide text-black transition-transform duration-200 hover:scale-[1.03]"
              >
                <svg viewBox="0 0 384 512" width="16" height="16" fill="currentColor" aria-hidden="true" className="flex-shrink-0">
                  <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
                </svg>
                Download on the App Store
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.base69c9edead8ac869e71b0e3ba.app"
                target="_blank"
                rel="noreferrer"
                data-testid="footer-android-btn"
                className="inline-flex items-center justify-center gap-3 border border-teal bg-transparent px-6 py-3 font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-teal/10 hover:text-teal"
              >
                <svg viewBox="0 0 512 512" width="18" height="18" aria-hidden="true" className="flex-shrink-0">
                  <path fill="#00A0FF" d="M25.6 24.3c-4 4.3-6.4 11-6.4 19.6v424.2c0 8.6 2.4 15.3 6.4 19.6l1.4 1.4 237.6-237.6v-5.6L27 22.9l-1.4 1.4z" />
                  <path fill="#00E676" d="M344.9 335.6l-79.3-79.3v-5.6l79.3-79.3 1.8 1L440.9 227c26.9 15.3 26.9 40.3 0 55.6l-94.2 53.6-1.8 1z" />
                  <path fill="#FF3A44" d="M346.7 334.6L265.6 253.5 25.6 493.5c8.9 9.4 23.5 10.5 40 1.2l281.1-160.1z" />
                  <path fill="#FFC107" d="M346.7 172.4L65.6 12.3c-16.5-9.4-31.1-8.2-40 1.2l240 240 81.1-81.1z" />
                </svg>
                Download for Android
              </a>
            </div>
          </div>

          <div className="flex flex-col items-start gap-6 md:items-end">
            <div className="flex gap-3">
              {[
                { Icon: Instagram, href: "https://www.instagram.com/trainova.app", label: "instagram" },
                { Icon: Facebook, href: "https://www.facebook.com", label: "facebook" },
                { Icon: TikTok, href: "https://www.tiktok.com/@trainova?_r=1&_t=ZP-99PZIs4rrUl", label: "tiktok" },
              ].map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`footer-social-${label}`}
                  className="flex h-11 w-11 items-center justify-center border border-white/15 text-zinc-300 transition-colors duration-200 hover:border-teal hover:text-teal"
                  aria-label={label}
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
            <a
              href="#top"
              data-testid="footer-back-to-top"
              className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-zinc-400 transition-colors duration-200 hover:text-teal"
            >
              Back to top <ArrowUp size={16} />
            </a>
          </div>
        </div>

        <div className="mt-16 grid gap-8 border-t border-white/10 pt-10 md:grid-cols-[1fr_auto]">
          <nav className="flex flex-wrap gap-x-8 gap-y-3" aria-label="Legal">
            {PAGE_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`footer-link-${l.to.slice(1)}`}
                className="text-sm uppercase tracking-widest text-zinc-400 transition-colors duration-200 hover:text-teal"
              >
                {l.label}
              </Link>
            ))}
          </nav>
        </div>

        <div className="mt-10 flex flex-col justify-between gap-4 border-t border-white/10 pt-8 text-sm text-zinc-600 md:flex-row">
          <span>© {new Date().getFullYear()} Trainova LLC. All rights reserved.</span>
          <span>Train better. Together.</span>
        </div>
      </div>
    </footer>
  );
}
